import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import isBrowser from 'util/isBrowser';
import { isImpersonated } from 'util/api/auth';
import { BASE_URL, headers } from 'util/api/client';
import { EventProperties } from 'ahoy.js';

type AhoyContextProps = {
  trackEvent: (event: string, properties?: Record<string, unknown>) => void;
  trackView: (additionalProperties?: EventProperties) => void;
};

const AhoyContext = createContext<AhoyContextProps>(null);

export const AhoyProvider = (props: PropsWithChildren<NonNullable<unknown>>) => {
  const { children } = props;

  const [ahoy, setAhoy] = useState<{
    track: AhoyContextProps['trackEvent'];
    trackView: AhoyContextProps['trackView'];
  }>(null);

  useEffect(() => {
    if (isBrowser() && !isImpersonated()) {
      void import('ahoy.js').then((ahoyModule) => {
        const ahoySetup = ahoyModule.default;

        ahoySetup.configure({
          withCredentials: true,
          urlPrefix: BASE_URL,
          headers: headers(),
        });

        setAhoy(ahoySetup);
      });
    }
  }, []);

  const trackEvent = useCallback(
    (name: string, payload: Record<string, unknown>) => {
      if (ahoy) {
        return ahoy.track(name, payload);
      }

      // eslint-disable-next-line no-console
      return console.info('[AHOY DEV] trackEvent', name, payload);
    },
    [ahoy],
  );

  const trackView = useCallback(() => {
    if (ahoy) {
      return ahoy.trackView({
        headers: headers(),
      });
    }

    // eslint-disable-next-line no-console
    return console.info('[AHOY DEV] trackView');
  }, [ahoy]);

  return <AhoyContext.Provider value={{ trackEvent, trackView }}>{children}</AhoyContext.Provider>;
};

export const useAhoy = () => useContext(AhoyContext);
